import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close,
  Save,
  Phone,
  Email,
  WhatsApp,
  Business,
  Apartment,
  Schedule,
  LocationOn,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { formatarPhoneOnInput } from '../../utils/formatToView';

interface Contato {
  id?: number;
  tipo: 'canal_atendimento' | 'escritorios' | 'departamentos';
  tipocanal?: 'email' | 'whatsapp' | 'telefone';
  numero?: string;
  email?: string;
  horario_atendimento?: string;
  endereco?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  coordenadas?: string;
  descricao?: string;
  status: 'ativo' | 'inativo';
  ordem: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ContatoFormModalProps {
  open: boolean;
  onClose: () => void;
  tipo: 'canal_atendimento' | 'escritorios' | 'departamentos';
  contato?: Contato | null;
  onSave: (contato: Contato) => Promise<void>;
}

const ContatoFormModal: React.FC<ContatoFormModalProps> = ({
  open,
  onClose,
  tipo,
  contato,
  onSave,
}) => {
  const [formData, setFormData] = useState<Contato>({
    tipo,
    tipocanal: 'telefone',
    numero: '',
    email: '',
    horario_atendimento: '',
    endereco: '',
    bairro: '',
    cidade: '',
    estado: '',
    coordenadas: '',
    descricao: '',
    status: 'ativo',
    ordem: 0,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const getTipoLabel = (tipo: string) => {
    const labels = {
      canal_atendimento: 'Canal de Atendimento',
      escritorios: 'Escritório',
      departamentos: 'Departamento',
    };
    return labels[tipo as keyof typeof labels] || tipo;
  };

  const getTipocanalIcon = (tipocanal: string) => {
    switch (tipocanal) {
      case 'telefone':
        return <Phone fontSize="small" />;
      case 'email':
        return <Email fontSize="small" />;
      case 'whatsapp':
        return <WhatsApp fontSize="small" />;
      default:
        return null;
    }
  };

  const getTipocanalColor = (tipocanal: string) => {
    switch (tipocanal) {
      case 'telefone':
        return 'primary';
      case 'email':
        return 'info';
      case 'whatsapp':
        return 'success';
      default:
        return 'default';
    }
  };

  // Campos obrigatórios por tipo
  const getRequiredFields = () => {
    switch (tipo) {
      case 'canal_atendimento':
        return ['tipocanal', 'horario_atendimento'];
      case 'escritorios':
        return ['endereco', 'cidade', 'estado', 'horario_atendimento'];
      case 'departamentos':
        return ['horario_atendimento'];
      default:
        return [];
    }
  };

  // Campos condicionais por tipo
  const getConditionalFields = () => {
    switch (tipo) {
      case 'canal_atendimento':
        return formData.tipocanal === 'email' ? ['email'] : ['numero'];
      case 'escritorios':
        return ['telefone', 'email'];
      case 'departamentos':
        return ['telefone', 'email'];
      default:
        return [];
    }
  };

  // Validação do formulário
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const requiredFields = getRequiredFields();
    const conditionalFields = getConditionalFields();

    // Validar campos obrigatórios
    requiredFields.forEach((field) => {
      if (!formData[field as keyof Contato] || String(formData[field as keyof Contato]).trim() === '') {
        newErrors[field] = 'Campo obrigatório';
      }
    });

    // Validar campos condicionais
    conditionalFields.forEach((field) => {
      if (field === 'email' && formData.tipocanal === 'email') {
        if (!formData.email || formData.email.trim() === '') {
          newErrors.email = 'Email é obrigatório para canal de email';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Email inválido';
        }
      } else if (field === 'numero' && (formData.tipocanal === 'telefone' || formData.tipocanal === 'whatsapp')) {
        if (!formData.numero || formData.numero.trim() === '') {
          newErrors.numero = 'Número é obrigatório para telefone/WhatsApp';
        }
      } else if (field === 'telefone' && tipo !== 'canal_atendimento') {
        if (!formData.numero || formData.numero.trim() === '') {
          newErrors.numero = 'Telefone é obrigatório';
        }
      } else if (field === 'email' && tipo !== 'canal_atendimento') {
        if (!formData.email || formData.email.trim() === '') {
          newErrors.email = 'Email é obrigatório';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Email inválido';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Carregar dados do contato para edição
  useEffect(() => {
    if (contato) {
      setFormData({
        ...contato,
        tipo,
      });
    } else {
      setFormData({
        tipo,
        tipocanal: 'telefone',
        numero: '',
        email: '',
        horario_atendimento: '',
        endereco: '',
        bairro: '',
        cidade: '',
        estado: '',
        coordenadas: '',
        descricao: '',
        status: 'ativo',
        ordem: 0,
      });
    }
    setErrors({});
  }, [contato, tipo, open]);

  // Limpar formulário ao fechar
  const handleClose = () => {
    setFormData({
      tipo,
      tipocanal: 'telefone',
      numero: '',
      email: '',
      horario_atendimento: '',
      endereco: '',
      bairro: '',
      cidade: '',
      estado: '',
      coordenadas: '',
      status: 'ativo',
      ordem: 0,
    });
    setErrors({});
    onClose();
  };

  // Salvar formulário
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(formData);
      toast.success(`${getTipoLabel(tipo)} ${contato ? 'atualizado' : 'criado'} com sucesso!`);
      handleClose();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Atualizar campo do formulário
  const handleFieldChange = (field: keyof Contato, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro do campo quando alterado
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Renderizar campos específicos por tipo
  const renderTipoSpecificFields = () => {
    switch (tipo) {
      case 'canal_atendimento':
        return (
          <>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.tipocanal}>
                <InputLabel>Tipo de Canal</InputLabel>
                <Select
                  value={formData.tipocanal}
                  label="Tipo de Canal"
                  onChange={(e) => handleFieldChange('tipocanal', e.target.value)}
                >
                  <MenuItem value="telefone">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" />
                      Telefone
                    </Box>
                  </MenuItem>
                  <MenuItem value="whatsapp">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WhatsApp fontSize="small" />
                      WhatsApp
                    </Box>
                  </MenuItem>
                  <MenuItem value="email">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" />
                      Email
                    </Box>
                  </MenuItem>
                </Select>
                {errors.tipocanal && (
                  <Typography variant="caption" color="error">
                    {errors.tipocanal}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {formData.tipocanal === 'email' ? (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  placeholder="exemplo@empresa.com"
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={formData.tipocanal === 'whatsapp' ? 'Número WhatsApp' : 'Número Telefone'}
                  value={formData.numero}
                  onChange={(e) => {
                    const limit = formData.tipocanal === 'whatsapp' ? 11 : 10;
                    handleFieldChange('numero', formatarPhoneOnInput(e.target.value, limit));
                  }}
                  error={!!errors.numero}
                  helperText={errors.numero}
                  placeholder={formData.tipocanal === 'whatsapp' ? '(11) 99999-9999' : '(11) 3333-3333'}
                />
              </Grid>
            )}
          </>
        );

      case 'escritorios':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => handleFieldChange('descricao', e.target.value)}
                placeholder="Descreva as atividades e responsabilidades deste escritório"
                multiline
                rows={3}
                helperText="Descrição detalhada das atividades e responsabilidades"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Endereço"
                value={formData.endereco}
                onChange={(e) => handleFieldChange('endereco', e.target.value)}
                error={!!errors.endereco}
                helperText={errors.endereco}
                placeholder="Rua, número, complemento"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Bairro"
                value={formData.bairro}
                onChange={(e) => handleFieldChange('bairro', e.target.value)}
                placeholder="Nome do bairro"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.cidade}
                onChange={(e) => handleFieldChange('cidade', e.target.value)}
                error={!!errors.cidade}
                helperText={errors.cidade}
                placeholder="Nome da cidade"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Estado"
                value={formData.estado}
                onChange={(e) => handleFieldChange('estado', e.target.value)}
                error={!!errors.estado}
                helperText={errors.estado}
                placeholder="UF"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Coordenadas"
                value={formData.coordenadas}
                onChange={(e) => handleFieldChange('coordenadas', e.target.value)}
                placeholder="latitude, longitude"
                helperText="Ex: -23.5505,-46.6333"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={formData.numero}
                onChange={(e) => handleFieldChange('numero', formatarPhoneOnInput(e.target.value, 10))}
                error={!!errors.numero}
                helperText={errors.numero}
                placeholder="(11) 3333-3333"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="exemplo@empresa.com"
              />
            </Grid>


          </>
        );

      case 'departamentos':
        return (
          <>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={formData.descricao}
                onChange={(e) => handleFieldChange('descricao', e.target.value)}
                placeholder="Descreva as atividades e responsabilidades deste departamento"
                multiline
                rows={3}
                helperText="Descrição detalhada das atividades e responsabilidades"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone"
                value={formData.numero}
                onChange={(e) => handleFieldChange('numero', formatarPhoneOnInput(e.target.value, 11))}
                error={!!errors.numero}
                helperText={errors.numero}
                placeholder="(11) 3333-3333"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                placeholder="exemplo@empresa.com"
              />
            </Grid>


          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tipo === 'escritorios' ? (
            <Business color="primary" />
          ) : tipo === 'departamentos' ? (
            <Apartment color="primary" />
          ) : (
            <Phone color="primary" />
          )}
          {contato ? 'Editar' : 'Cadastrar'} {getTipoLabel(tipo)}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Campos específicos por tipo */}
          {renderTipoSpecificFields()}

          {/* Campo comum: Horário de Atendimento */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Horário de Atendimento"
              value={formData.horario_atendimento}
              onChange={(e) => handleFieldChange('horario_atendimento', e.target.value)}
              error={!!errors.horario_atendimento}
              helperText={errors.horario_atendimento || 'Ex: Segunda a Sexta: 8h às 18h | Sábado: 8h às 12h'}
              placeholder="Digite o horário de atendimento"
              multiline
              rows={2}
            />
          </Grid>

          {/* Campo comum: Status */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Campo comum: Ordem */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Ordem"
              type="number"
              value={formData.ordem}
              onChange={(e) => handleFieldChange('ordem', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0 }}
              helperText="Ordem de exibição (0 = automático)"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
        >
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContatoFormModal;
